import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItemButton,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Forum as ForumIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  SupportAgent as SupportAgentIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';

interface MyTicket {
  id: string;
  ticket_number: string | null;
  subject: string;
  description: string;
  status: string;
  priority: string;
  category: string;
  resolution: string | null;
  created_at: string;
  updated_at: string | null;
}

interface TicketMessage {
  id: string;
  support_ticket_id: string;
  sender_type: string;
  message_content: string;
  created_at: string;
}

interface MyTicketsProps {
  /** Bumping this value triggers a reload (e.g. after submitting a new ticket). */
  refreshSignal?: number;
}

const STATUS_COLORS: Record<string, 'default' | 'info' | 'warning' | 'success'> = {
  open: 'info',
  in_progress: 'warning',
  resolved: 'success',
  closed: 'default',
};

const formatStatus = (status: string) =>
  status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatDateTime = (value: string) => {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
};

const MyTickets: React.FC<MyTicketsProps> = ({ refreshSignal }) => {
  const [tickets, setTickets] = useState<MyTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<MyTicket | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [threadError, setThreadError] = useState<string | null>(null);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getMyTickets();
      if (response.success && Array.isArray(response.data)) {
        setTickets(response.data);
      } else {
        setError(response.message || 'Unable to load your support requests.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load your support requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets, refreshSignal]);

  const loadMessages = useCallback(async (ticketId: string) => {
    setMessagesLoading(true);
    setThreadError(null);
    try {
      const response = await apiService.getMyTicketMessages(ticketId);
      if (response.success && Array.isArray(response.data)) {
        setMessages(response.data);
      } else {
        setThreadError(response.message || 'Unable to load the conversation.');
      }
    } catch (err) {
      setThreadError(err instanceof Error ? err.message : 'Unable to load the conversation.');
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  const openTicket = (ticket: MyTicket) => {
    setSelected(ticket);
    setReply('');
    setThreadError(null);
    setMessages([]);
    loadMessages(ticket.id);
  };

  const closeTicket = () => {
    setSelected(null);
    setMessages([]);
    setReply('');
  };

  const handleSendReply = async () => {
    if (!selected || !reply.trim()) return;
    setSending(true);
    setThreadError(null);
    try {
      const response = await apiService.replyToMyTicket(selected.id, reply.trim());
      if (response.success && response.data) {
        setReply('');
        await loadMessages(selected.id);
        // The reply may reopen a resolved ticket, so refresh the list too.
        loadTickets();
      } else {
        setThreadError(response.message || 'Unable to send your reply.');
      }
    } catch (err) {
      setThreadError(err instanceof Error ? err.message : 'Unable to send your reply.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <ForumIcon color="primary" />
            <Typography variant="h6" component="h2">
              My Support Requests
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={loadTickets} disabled={loading} size="small">
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Typography variant="body2" color="text.secondary" paragraph>
          Track your requests and read replies from our support team here.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : tickets.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            You haven't submitted any support requests yet.
          </Typography>
        ) : (
          <List disablePadding>
            {tickets.map((ticket, idx) => (
              <React.Fragment key={ticket.id}>
                {idx > 0 && <Divider component="li" />}
                <ListItemButton onClick={() => openTicket(ticket)} sx={{ alignItems: 'flex-start', py: 1.5 }}>
                  <Box flexGrow={1} minWidth={0}>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography variant="subtitle2" noWrap>
                        {ticket.subject}
                      </Typography>
                      <Chip
                        size="small"
                        label={formatStatus(ticket.status)}
                        color={STATUS_COLORS[ticket.status] ?? 'default'}
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {ticket.ticket_number ? `${ticket.ticket_number} • ` : ''}
                      Opened {formatDateTime(ticket.created_at)}
                    </Typography>
                  </Box>
                </ListItemButton>
              </React.Fragment>
            ))}
          </List>
        )}
      </CardContent>

      <Dialog open={Boolean(selected)} onClose={closeTicket} fullWidth maxWidth="sm">
        {selected && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                {selected.subject}
                <Chip
                  size="small"
                  label={formatStatus(selected.status)}
                  color={STATUS_COLORS[selected.status] ?? 'default'}
                  variant="outlined"
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {selected.ticket_number ? `${selected.ticket_number} • ` : ''}
                Opened {formatDateTime(selected.created_at)}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              {/* Original request */}
              <Box mb={2}>
                <Typography variant="caption" color="text.secondary">
                  Your original request
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                  {selected.description}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {threadError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setThreadError(null)}>
                  {threadError}
                </Alert>
              )}

              {messagesLoading ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : messages.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No replies yet. Our team will respond here.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {messages.map((message) => {
                    const isUser = message.sender_type === 'user';
                    return (
                      <Box
                        key={message.id}
                        sx={{
                          alignSelf: isUser ? 'flex-end' : 'flex-start',
                          maxWidth: '85%',
                          bgcolor: isUser ? 'primary.main' : 'action.hover',
                          color: isUser ? 'primary.contrastText' : 'text.primary',
                          borderRadius: 2,
                          px: 1.5,
                          py: 1,
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={0.5} mb={0.25}>
                          {isUser ? (
                            <PersonIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <SupportAgentIcon sx={{ fontSize: 16 }} />
                          )}
                          <Typography variant="caption" sx={{ opacity: 0.85 }}>
                            {isUser ? 'You' : 'Support'} • {formatDateTime(message.created_at)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {message.message_content}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </DialogContent>
            <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, p: 2 }}>
              <TextField
                label="Add a reply"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                disabled={sending}
                placeholder="Type your reply to support…"
              />
              <Box display="flex" justifyContent="flex-end" gap={1}>
                <Button onClick={closeTicket} disabled={sending}>
                  Close
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSendReply}
                  disabled={sending || !reply.trim()}
                  startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                >
                  {sending ? 'Sending…' : 'Send Reply'}
                </Button>
              </Box>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Card>
  );
};

export default MyTickets;
